<template>
  <article>
    <div v-if="title" class="chart-title">
      {{ title }}
    </div>
    <pie-chart
      :chart-data="chartData"
      :chart-options="chartOptions"
      :plugins="[htmlLegendPlugin]"
    />
    <div :id="legendContainerId" class="chart-legend-container mt-8"></div>
  </article>
</template>

<script>
  import { Pie as PieChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    CategoryScale,
  } from 'chart.js'

  ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

  export default {
    name: 'RevenuePieChart',
    components: { PieChart },
    props: {
      legendContainerId: String,
      items: {
        type: Array,
        default: () => [],
      },
      colors: {
        type: Array,
        default: () => [],
      },
      title: {
        type: String,
        default: null,
      },
      labelClassList: {
        type: Array,
        default: () => ['col-md-4', 'col-12'],
      },
    },
    data () {
      return {
        htmlLegendPlugin: {
          id: 'htmlLegend',
          afterUpdate (chart, args, options) {
            const legendContainer = document.getElementById(options.containerID)

            // Remove old legend items
            while (legendContainer.firstChild) {
              legendContainer.firstChild.remove()
            }

            const items = chart.options.plugins.legend.labels.generateLabels(
              chart,
            )

            items.forEach((item, index) => {
              const label = document.createElement('div')
              label.style.alignItems = 'center'
              label.style.cursor = 'pointer'
              label.style.display = 'inline-flex'
              label.style.flexDirection = 'row'
              label.style.margin = '10px 0'

              if (index !== items.length - 1) {
                label.style.marginRight = '10px'
              }

              label.onclick = () => {
                chart.toggleDataVisibility(item.index)
                chart.update()
              }

              const circle = document.createElement('div')
              circle.style.background = item.fillStyle
              circle.style.borderColor = item.strokeStyle
              circle.style.borderWidth = item.lineWidth + 'px'
              circle.style.display = 'inline-flex'
              circle.style.alignItems = 'center'
              circle.style.justifyContent = 'center'
              circle.style.flexShrink = 0
              circle.style.height = '45px'
              circle.style.marginRight = '10px'
              circle.style.width = '45px'
              circle.style.borderRadius = '50%'

              const percentSpan = document.createElement('span')
              percentSpan.style.color = '#fff'
              percentSpan.style.fontSize = '12px'
              percentSpan.textContent =
                chart.data.datasets[0].data[index].percent + '%'

              circle.appendChild(percentSpan)

              const textContainer = document.createElement('p')
              textContainer.style.color = item.fontColor
              textContainer.style.margin = 0
              textContainer.style.padding = 0
              textContainer.style.textDecoration = item.hidden
                ? 'line-through'
                : ''

              const text = document.createTextNode(item.text)
              textContainer.appendChild(text)

              label.appendChild(circle)
              label.appendChild(textContainer)
              legendContainer.appendChild(label)
            })
          },
        },
      }
    },
    computed: {
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          labelClassList: this.labelClassList,
          parsing: {
            key: 'percent',
          },
          plugins: {
            legend: {
              display: false,
            },
            htmlLegend: {
              containerID: this.legendContainerId,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.label || ''

                  if (label) {
                    label += ': '
                  }

                  if (context.raw.formattedValue) {
                    label += context.raw.formattedValue
                  }

                  return label
                },
              },
            },
          },
        }
      },
      chartData () {
        return {
          labels: this.items.map((item) => item.name),
          datasets: [
            {
              labels: this.items.map((item) => item.name),
              backgroundColor: this.colors,
              data: this.items,
            },
          ],
        }
      },
    },
  }
</script>

<style lang="less" scoped>
@import '@/assets/styles/statistic.less';

.chart-legend-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 500px) {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
